import LegalLayout, { LegalSection, LegalList } from "@/pages/legal/LegalLayout";

const TOC = [
  { id: "overview", label: "Overview" },
  { id: "collect", label: "Information we collect" },
  { id: "account", label: "Account & profile information" },
  { id: "sensitive", label: "Sensitive content" },
  { id: "how-we-use", label: "How we use information" },
  { id: "storage", label: "Data storage & security" },
  { id: "sharing", label: "Sharing with third parties" },
  { id: "analytics", label: "Analytics & cookies" },
  { id: "rights", label: "Your rights" },
  { id: "children", label: "Children's privacy" },
  { id: "changes", label: "Changes to this policy" },
  { id: "contact", label: "Contact" },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testid="privacy-page"
      title="Privacy Policy"
      intro="This Privacy Policy explains what information Relyn collects, how we use it, and the choices you have. We designed Relyn privacy-first, and we treat your reflections as yours."
      effectiveDate="July 11, 2026"
      lastUpdated="July 11, 2026"
      toc={TOC}
    >
      <LegalSection id="overview" title="Overview">
        <p>
          Relyn is a personal growth product designed to help men understand themselves,
          strengthen relationships, and grow with guidance from Leo, our AI companion.
          We are committed to handling your information responsibly.
        </p>
        <p>
          This policy applies to relynapp.com, the Relyn mobile applications, and any
          Relyn service that links to this policy (collectively, the "Service").
        </p>
      </LegalSection>

      <LegalSection id="collect" title="Information we collect">
        <p>We collect only what we need to run Relyn well and to help you grow.</p>
        <LegalList
          items={[
            "Information you provide directly, such as your name, email address, and profile preferences.",
            "Content you create in the app, including mood entries, journal entries, goals, challenges, and messages you send to Leo.",
            "Relationship logs and notes you choose to record about the people in your life.",
            "Technical information necessary to operate the Service, such as device type, operating system, IP address, and diagnostic logs.",
            "Interactions with our marketing surfaces, such as when you join the waitlist or contact us.",
          ]}
        />
      </LegalSection>

      <LegalSection id="account" title="Account and profile information">
        <p>
          When you create a Relyn account we collect basic information needed to secure
          and personalize your experience: your name, email address, authentication
          credentials, and optional profile details you provide. You can update this
          information at any time from your account settings.
        </p>
      </LegalSection>

      <LegalSection id="sensitive" title="Sensitive user content">
        <p>
          Relyn treats your <strong>private journal entries, mood information,
          relationship logs, and conversations with Leo</strong> as sensitive user content.
        </p>
        <LegalList
          items={[
            "We do not sell your sensitive content to any third party.",
            "We do not use your private journal entries or Leo conversations for advertising.",
            "Access to sensitive content is limited to systems required to operate the Service on your behalf.",
            "Sensitive content is stored using encryption at rest and transmitted using encryption in transit where supported.",
          ]}
        />
      </LegalSection>

      <LegalSection id="how-we-use" title="How we use information">
        <p>We use the information we collect to:</p>
        <LegalList
          items={[
            "Provide, maintain, and improve the Service.",
            "Personalize check-ins, insights, prompts, and Leo's guidance so they feel useful to you.",
            "Communicate with you about product updates, security, and service changes.",
            "Prevent fraud, abuse, and violations of our Terms of Service.",
            "Meet legal, regulatory, and safety obligations.",
          ]}
        />
      </LegalSection>

      <LegalSection id="storage" title="Data storage and security">
        <p>
          Your information is stored on servers operated by reputable cloud infrastructure
          providers. We use industry-standard technical and organizational safeguards —
          including encryption in transit and at rest where supported, access controls,
          monitoring, and periodic reviews — to protect your data.
        </p>
        <p>
          No online service can guarantee absolute security. If we ever detect an incident
          that affects your information, we will notify affected users in accordance with
          applicable law.
        </p>
      </LegalSection>

      <LegalSection id="sharing" title="Sharing with third parties">
        <p>We do not sell your personal information. We share information only:</p>
        <LegalList
          items={[
            "With trusted service providers (such as cloud hosting, analytics, and AI model providers) who process data on our behalf under contractual privacy and security obligations.",
            "When required by law, subpoena, or valid legal process, or to protect the rights, property, or safety of Relyn, our users, or the public.",
            "In connection with a merger, acquisition, or sale of assets, in which case any successor will be bound by this policy.",
          ]}
        />
        <p>
          Leo, our AI companion, is powered by third-party AI model providers we use to
          generate its responses. When you send a message to Leo, the content of that
          message is transmitted to the provider that is generating the response so it
          can be processed. Each provider handles data in accordance with its own
          privacy and data-handling policies. We choose providers that we believe offer
          strong privacy and security practices, and we regularly review these choices
          as our stack evolves. If provider-specific protections around retention or
          use for model training become a material part of how Relyn operates, we will
          document them clearly here.
        </p>
      </LegalSection>

      <LegalSection id="analytics" title="Analytics and cookies">
        <p>
          We use a small number of analytics tools to understand how the Service is used
          so we can improve it. These tools collect aggregated, non-identifying usage
          information such as page views, feature usage, and performance metrics.
        </p>
        <p>
          See our{" "}
          <a href="/cookies" className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4">
            Cookie Policy
          </a>{" "}
          for details on the specific cookies we use and how to manage them.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="Your rights">
        <p>You have the right to:</p>
        <LegalList
          items={[
            "Access the personal information we hold about you.",
            "Correct information that is inaccurate or incomplete.",
            "Export a copy of your information in a portable format.",
            "Delete your account and associated personal information, subject to limited exceptions required by law.",
            "Object to or restrict certain processing, and withdraw consent where processing is based on consent.",
          ]}
        />
        <p>
          To exercise any of these rights, email us at{" "}
          <a href="mailto:support@relynapp.com" className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4">
            support@relynapp.com
          </a>
          . We will respond within a reasonable timeframe and in accordance with
          applicable law.
        </p>
      </LegalSection>

      <LegalSection id="children" title="Children's privacy">
        <p>
          Relyn is intended for use by adults 18 years of age or older. We do not
          knowingly collect personal information from anyone under 18. If we learn that
          we have collected information from a child under 18, we will delete it. If you
          believe a child has provided us with personal information, please contact us.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we will
          update the "Last updated" date at the top of this page. Material changes will
          be communicated through the Service or by email before they take effect.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          For privacy questions, legal inquiries, or requests concerning your personal
          information, please contact us at{" "}
          <a
            href="mailto:support@relynapp.com"
            className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4"
            data-testid="legal-contact-email"
          >
            support@relynapp.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
